import React, { useEffect, useRef } from 'react';
import type {
	DefaultValues,
	FieldValues,
	Path,
	UseFormReturn,
} from 'react-hook-form';
import * as yup from 'yup';
import {
	type FormDefinition,
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import { useAuthorizationConsentDefinition } from '@gov-nx/core/hooks/AuthorizationConsent/useAuthorizationConsentDefinition';
import type { StreetNumbersFormDefinitions } from '@gov-nx/core/hooks/StreetNumbers/useStreetNumbersFormDefinitions';
import { useFormStore } from '@gov-nx/store/forms/store';
import type { AutocompleteDefinition } from '@gov-nx/utils/common/Form/autocomplete';
import type { BadgeListDefinition } from '@gov-nx/utils/common/Form/badge-list';
import type { CheckboxDefinition } from '@gov-nx/utils/common/Form/checkbox';
import type { DateDefinition } from '@gov-nx/utils/common/Form/date';
import type { FileUploadDefinition } from '@gov-nx/utils/common/Form/file-upload';
import type { MultiselectDefinition } from '@gov-nx/utils/common/Form/multiselect';
import type { PhoneDefinition } from '@gov-nx/utils/common/Form/phone';
import type { RadioDefinition } from '@gov-nx/utils/common/Form/radio';
import type { SelectDefinition } from '@gov-nx/utils/common/Form/select';
import type { InputDefinition } from '@gov-nx/utils/common/Form/string';

export interface FormDefinition2<
	T extends FieldValues,
	Fields,
> extends FormDefinition<T> {
	fields: Fields;
}

type Field =
	| InputDefinition
	| BadgeListDefinition
	| RadioDefinition
	| DateDefinition
	| CheckboxDefinition
	| SelectDefinition
	| PhoneDefinition
	| AutocompleteDefinition
	| MultiselectDefinition
	| FileUploadDefinition
	| StreetNumbersFormDefinitions;

const getDependentFields = (definitions: Field[]): Record<string, string[]> =>
	definitions.reduce<Record<string, string[]>>((all, { schema, field }) => {
		schema.deps.forEach((dependency) => {
			all[dependency] = [...(all[dependency] ?? []), field.field.name];
		});

		return all;
	}, {});

/** The resolver only writes back the error of the field that changed, so an error
 * validated against another field stays displayed after that field makes it valid. */
const useDependentFieldsRevalidation = <Form extends FieldValues>(
	formMethods: UseFormReturn<Form>,
	definitions: Field[]
) => {
	const dependentFields = useRef<Record<string, string[]>>({});
	dependentFields.current = getDependentFields(definitions);

	useEffect(() => {
		const subscription = formMethods.watch((_, { name }) => {
			const dependents = name ? dependentFields.current[name] : undefined;

			dependents?.forEach((dependent) => {
				const path = dependent as Path<Form>;

				if (formMethods.getFieldState(path).invalid) {
					void formMethods.trigger(path);
				}
			});
		});

		return () => subscription.unsubscribe();
	}, [formMethods]);
};

/**
 * This is new form builder which should be used for building all the forms, It is not perfect yet but it is a start
 */
export const useFormBuilder = <Form extends FieldValues, Fields>(
	definitions: Field[]
): FormDefinition2<Form, Fields> => {
	const schema = definitions.reduce(
		(all, field) => ({ ...all, [field.field.field.name]: field.schema }),
		{} as Record<string, yup.AnySchema>
	);

	const formSchema = yup.object(schema).required() as yup.ObjectSchema<Form>;

	const defaultValues = definitions.reduce(
		(all, field) => ({ ...all, [field.field.field.name]: field.defaultValue }),
		{} as DefaultValues<Form>
	);

	const formMethods = useGovForm<Form>({
		formSchema,
		defaultValues,
	});

	useDependentFieldsRevalidation(formMethods, definitions);

	const formDefinition = getFormDefinition<Form>({ formMethods, formSchema });

	const fields = definitions.reduce(
		(all, field) => ({ ...all, [field.field.field.name]: field.field }),
		{} as Fields
	);

	return {
		...formDefinition,
		fields,
	};
};

export const useFormBuilderWithConsent = <Form extends FieldValues, Fields>(
	definitions: Field[],
	options: { isLowAuthorization: boolean; isDisabled: boolean }
): FormDefinition2<Form, Fields> => {
	// Always call the hook unconditionally to satisfy React's Rules of Hooks,
	// but only include the field in the form when consent is actually required.
	// This prevents __souhlas from polluting form state (and default payloads)
	// for flows where authorization consent is not needed.
	const consentDefinition = useAuthorizationConsentDefinition({
		isDisabled: options.isDisabled,
		isConsentRequired: options.isLowAuthorization,
	});

	const allDefinitions = options.isLowAuthorization
		? [...definitions, consentDefinition]
		: definitions;

	return useFormBuilder<Form, Fields>(allDefinitions);
};

export const usePersistedForm = (persistKey: string) => {
	const storeUpdate = useFormStore('update');
	const storeReset = useFormStore('reset');
	const storeValues = useFormStore(persistKey);

	return function usePersistedForm<Form extends FieldValues, Fields>(
		formDefinition: FormDefinition2<Form, Fields>
	): FormDefinition2<Form, Fields> {
		React.useEffect(() => {
			const subscription = formDefinition.formMethods.watch((value) => {
				if (storeValues !== value) {
					storeUpdate(persistKey, value);
				}
			});
			return () => subscription.unsubscribe();
		}, [formDefinition.formMethods]);

		// Set default values from store if they exist
		useEffect(() => {
			if (storeValues) {
				formDefinition.formMethods.reset(storeValues as Form);
			}
		}, [formDefinition.formMethods]);

		return {
			...formDefinition,
			formReset: () => {
				// override formReset to also reset store
				formDefinition.formReset();
				storeReset(persistKey);
			},
		};
	};
};
